/**
 * Main browser tasks page for managing browser automation tasks
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Filter, Search, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { TaskCard, TaskForm, SessionManager, ErrorBoundary, TaskListSkeleton } from '../components/anchor-browser'
import { useTasks, useCreateTask, useStopTask, usePauseTask, useResumeTask } from '../hooks/use-anchor-browser'
import { useToast } from '../hooks/use-toast'
import type { CreateTaskRequest, TaskStatus } from '../types/anchor-browser'

const TASK_STATUSES: { value: TaskStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Tasks' },
  { value: 'running', label: 'Running' },
  { value: 'created', label: 'Created' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
  // Legacy browser-use statuses for compatibility
  { value: 'paused', label: 'Paused' },
  { value: 'finished', label: 'Finished' },
  { value: 'stopped', label: 'Stopped' },
]

function BrowserTasksContent() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all')
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  // Query parameters for filtering
  const queryParams = {
    status: statusFilter !== 'all' ? statusFilter : undefined,
    limit: 20,
  }

  const { data: tasksResponse, isLoading, error, refetch } = useTasks(queryParams)
  const createTaskMutation = useCreateTask()
  const stopTaskMutation = useStopTask()
  const pauseTaskMutation = usePauseTask()
  const resumeTaskMutation = useResumeTask()

  const tasks = tasksResponse?.tasks || []
  const filteredTasks = tasks.filter(task =>
    searchQuery === '' || 
    task.task.toLowerCase().includes(searchQuery.toLowerCase()) ||
    task.id.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleCreateTask = async (data: CreateTaskRequest & { session_id?: string; create_new_session?: boolean }) => {
    try {
      console.log('Creating task with data:', data)

      // Extract session info from the data
      const { session_id, create_new_session, ...taskData } = data

      // For now, we'll pass the session info in the task data
      // The backend will handle session creation/reuse logic
      const taskRequest = {
        ...taskData,
        session_id,
        create_new_session
      }

      const result = await createTaskMutation.mutateAsync(taskRequest)
      console.log('Task created successfully:', result)
      toast({
        title: 'Task Created',
        description: `Task ${result.id} has been created successfully.`,
      })
      setShowCreateDialog(false)
      navigate(`/browser-tasks/${result.id}`)
    } catch (error) {
      console.error('Failed to create task:', error)
      toast({
        title: 'Error',
        description: `Failed to create task: ${error instanceof Error ? error.message : 'Please try again.'}`,
        variant: 'destructive',
      })
    }
  }

  const handleTaskAction = async (
    action: 'stop' | 'pause' | 'resume',
    taskId: string
  ) => {
    try {
      switch (action) {
        case 'stop':
          await stopTaskMutation.mutateAsync(taskId)
          toast({ title: 'Task Stopped', description: `Task ${taskId} has been stopped.` })
          break
        case 'pause':
          await pauseTaskMutation.mutateAsync(taskId)
          toast({ title: 'Task Paused', description: `Task ${taskId} has been paused.` })
          break
        case 'resume':
          await resumeTaskMutation.mutateAsync(taskId)
          toast({ title: 'Task Resumed', description: `Task ${taskId} has been resumed.` })
          break
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${action} task. Please try again.`,
        variant: 'destructive',
      })
    }
  }

  const getStatusCounts = () => {
    const counts = tasks.reduce((acc, task) => {
      acc[task.status] = (acc[task.status] || 0) + 1
      return acc
    }, {} as Record<TaskStatus, number>)
    
    return counts
  }

  const statusCounts = getStatusCounts()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Browser Automation</h1>
              <p className="text-muted-foreground">
                Create and manage AI-powered browser automation tasks using custom prompts or saved personas
              </p>
            </div>
            {/* Webhook status removed - Anchor Browser uses polling */}
          </div>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Task
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Browser Automation Task</DialogTitle>
              <DialogDescription>
                Configure a new browser automation task using a custom prompt or select from your saved personas
              </DialogDescription>
            </DialogHeader>
            <TaskForm
              onSubmit={handleCreateTask}
              isLoading={createTaskMutation.isPending}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {TASK_STATUSES.slice(1).map((status) => (
          <Card key={status.value} className="cursor-pointer hover:shadow-md transition-shadow">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold">
                {statusCounts[status.value as TaskStatus] || 0}
              </div>
              <div className="text-sm text-muted-foreground">{status.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Session Management */}
      <SessionManager />

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as TaskStatus | 'all')}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                {TASK_STATUSES.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tasks List */}
      <div className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>
              Failed to load tasks. Please try refreshing the page.
            </AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <TaskListSkeleton count={3} />
        ) : filteredTasks.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <div className="text-muted-foreground">
                {searchQuery || statusFilter !== 'all' ? (
                  <>
                    <p className="text-lg mb-2">No tasks found</p>
                    <p>Try adjusting your search or filter criteria</p>
                  </>
                ) : (
                  <>
                    <p className="text-lg mb-2">No tasks yet</p>
                    <p className="mb-4">Create your first browser automation task to get started</p>
                    <Button onClick={() => setShowCreateDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Task
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onView={(taskId) => navigate(`/browser-tasks/${taskId}`)}
                onStop={(taskId) => handleTaskAction('stop', taskId)}
                onPause={(taskId) => handleTaskAction('pause', taskId)}
                onResume={(taskId) => handleTaskAction('resume', taskId)}
                isLoading={
                  stopTaskMutation.isPending ||
                  pauseTaskMutation.isPending ||
                  resumeTaskMutation.isPending
                }
              />
            ))}
          </div>
        )}

        {/* Load More */}
        {tasksResponse?.has_more && (
          <div className="text-center">
            <Button variant="outline">
              Load More Tasks
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export function BrowserTasks() {
  return (
    <ErrorBoundary>
      <BrowserTasksContent />
    </ErrorBoundary>
  )
}
